import { CheckboxListFilter } from 'components/SA/Filters'
import React, { FC, PropsWithChildren } from 'react'
import { updateClientSideUrl } from '../helpers/url'
import { FacetsConnector, FacetsConnectorProps } from 'connectors/Facets'
import { capitalizeString } from 'lib/helpers'
import { BaseFacetProps } from '../types'
import FilterFacetText from './FilterFacetText'
import styles from './CheckboxListFacet.module.scss'

type CheckboxListFacetProps = BaseFacetProps &
  FacetsConnectorProps & {
    facetName: string
    attributesList?: ReadonlyArray<string>
    overrideCheckboxTextRules?: Record<string, string>
    maxDisplayedItems?: number
    onFilterCallback?: (hrefParam: string) => void
    filterTextPlaceholder?: string
    onFilterFacetTextCallback?: (text: string) => void
  }

const CheckboxListFacet: FC<PropsWithChildren<CheckboxListFacetProps>> = ({
  baseUrl,
  nextAppRoute,
  facetName,
  attributesList,
  overrideCheckboxTextRules,
  maxDisplayedItems = 6,
  children,
  getHref,
  updateFacetValues,
  getIsSelected,
  getFacetNameAttributesList,
  onFilterCallback,
  filterTextPlaceholder,
  onFilterFacetTextCallback,
}) => {
  const facetNameAttributesList = attributesList || getFacetNameAttributesList(facetName)

  const handleFacetFilterClick = (hrefParam: string) => {
    updateClientSideUrl(hrefParam, nextAppRoute)
    onFilterCallback?.(hrefParam)
  }

  const filterOptions = facetNameAttributesList.map((attribute, index) => {
    const href = getHref({
      [facetName]: updateFacetValues(facetName, attribute),
      page: '', // Reset Page
    })
    const newUrlPath = `${baseUrl}${href}`
    const checkboxText = overrideCheckboxTextRules
      ? overrideCheckboxTextRules[attribute]
      : capitalizeString(attribute)

    return {
      text: checkboxText,
      checked: getIsSelected(facetName, attribute),
      onClick: () => handleFacetFilterClick(newUrlPath),
      isHidden: index >= maxDisplayedItems,
      href: newUrlPath,
    }
  })

  return (
    <div className={styles.facetListContainer}>
      {!!onFilterFacetTextCallback && (
        <FilterFacetText onSearch={onFilterFacetTextCallback} placeholder={filterTextPlaceholder} />
      )}
      {!filterOptions.length ? (
        <p className='defaultTypographySmall'>No Options Available</p>
      ) : (
        <CheckboxListFilter filterOptions={filterOptions} facetName={facetName}>
          {children}
        </CheckboxListFilter>
      )}
    </div>
  )
}

export default FacetsConnector(CheckboxListFacet)
